import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import { apiFetch, ApiError, tokenStorage } from '@/api/client'
import type { CurrentUser, LoginPayload, LoginResponse, RegisterPayload } from '@/api/types'

export interface AuthContextValue {
  user: CurrentUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  logout: () => void
  /** Re-fetch /api/me into context — call after any action that mutates the
   *  user (prestige claim, reputation shift) so useAuth() consumers refresh. */
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // On mount: if token in storage, validate it by fetching /me
  useEffect(() => {
    const token = tokenStorage.get()
    if (!token) {
      setIsLoading(false)
      return
    }

    apiFetch<CurrentUser>('/api/me')
      .then((u) => setUser(u))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          tokenStorage.clear()
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (payload: LoginPayload) => {
    const { token } = await apiFetch<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    tokenStorage.set(token)
    const u = await apiFetch<CurrentUser>('/api/me')
    setUser(u)
  }, [])

  const register = useCallback(async (payload: RegisterPayload) => {
    await apiFetch<CurrentUser>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    // Auto-login right after register
    await login({ email: payload.email, password: payload.password })
  }, [login])

  const logout = useCallback(() => {
    tokenStorage.clear()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    if (!tokenStorage.get()) return
    try {
      const u = await apiFetch<CurrentUser>('/api/me')
      setUser(u)
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 401) {
        tokenStorage.clear()
        setUser(null)
      }
      // other errors: keep the current user rather than logging out
    }
  }, [])

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    register,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
